import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import {
  Check, AlertCircle, Upload, Plus, Trash2, ExternalLink, Edit3,
  FileText, Users, Package, Palette, Globe, BookOpen, Settings,
  RefreshCw, Loader2, Link as LinkIcon, X,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const SECTION_DEFS = [
  { id: 'identity', title: 'Identita del progetto', icon: <Globe className="h-4 w-4" />, description: 'Chi sei e come ti presenti' },
  { id: 'brand', title: 'Brand & identita visiva', icon: <Palette className="h-4 w-4" />, description: 'Loghi, colori, tono di voce' },
  { id: 'channels', title: 'Canali e profili', icon: <LinkIcon className="h-4 w-4" />, description: 'Social e integrazioni API' },
  { id: 'products', title: 'Prodotti e servizi', icon: <Package className="h-4 w-4" />, description: 'Il tuo catalogo' },
  { id: 'documents', title: 'Knowledge Base', icon: <BookOpen className="h-4 w-4" />, description: 'Documenti di riferimento' },
  { id: 'rubriche', title: 'Rubriche editoriali', icon: <FileText className="h-4 w-4" />, description: 'Le tue rubriche di contenuto' },
  { id: 'team', title: 'Team e collaboratori', icon: <Users className="h-4 w-4" />, description: 'Chi lavora al progetto' },
];

function statusDot(complete) {
  return <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: complete ? '#22C55E' : '#F59E0B' }} />;
}

function integrationColor(s) {
  return s === 'connected' ? '#22C55E' : s === 'partial' ? '#F59E0B' : '#D1D5DB';
}

export default function WorkspacePage() {
  const [activeSection, setActiveSection] = useState('identity');
  const [profile, setProfile] = useState(null);
  const [channels, setChannels] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [products, setProducts] = useState([]);
  const [rubriche, setRubriche] = useState([]);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Editable form state
  const [form, setForm] = useState({});

  // Dialog states
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [profileForm, setProfileForm] = useState({ platform: 'LinkedIn', handle: '', url: '' });
  const [showRubricaDialog, setShowRubricaDialog] = useState(false);
  const [rubricaForm, setRubricaForm] = useState({ name: '', code: '', channel: 'LinkedIn personale', frequency: 'settimanale', day: 'Lunedi' });
  const [showCollabDialog, setShowCollabDialog] = useState(false);
  const [collabForm, setCollabForm] = useState({ name: '', email: '', role: 'editor' });
  const [showDocDialog, setShowDocDialog] = useState(false);
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({ name: '', category: 'formazione', product_type: 'workshop', pricing_model: 'per_partecipante', price: '', description: '', status: 'in_sviluppo' });

  const loadData = useCallback(async () => {
    try {
      const [profRes, chanRes, intRes, rubRes, pplRes] = await Promise.allSettled([
        api.get('/company/profile'),
        api.get('/social-profiles'),
        api.get('/integrations'),
        api.get('/content/rubriche'),
        api.get('/company/people'),
      ]);
      if (profRes.status === 'fulfilled') {
        const p = profRes.value.data;
        setProfile(p);
        setForm({
          name: p.name || '', tagline: p.tagline || '', description: p.description || '',
          sector: p.sector || 'Formazione', primaryMarket: p.primary_market || '',
          secondaryMarkets: p.secondary_markets || '', location: p.location || '',
          foundedYear: p.founded_year || '', languages: p.languages || ['Italiano'],
          positioning: p.positioning || '',
          currency: p.currency || 'CHF',
          primaryColor: p.brand?.primary_color || '#1A3A5C',
          accentColor: p.brand?.accent_color || '#2E75B6',
          backgroundColor: p.brand?.background_color || '#F5F5F5',
          primaryFont: p.brand?.primary_font || 'Inter',
          secondaryFont: p.brand?.secondary_font || 'Georgia',
          toneOfVoice: p.brand?.tone_of_voice || p.tone_of_voice || '',
          wordsToUse: p.brand?.words_to_use || p.keywords_use || [],
          wordsToAvoid: p.brand?.words_to_avoid || p.keywords_avoid || [],
        });
      }
      if (chanRes.status === 'fulfilled') setChannels(chanRes.value.data || []);
      if (intRes.status === 'fulfilled') {
        const intData = intRes.value.data;
        // API returns {integrations: [...], summary: {...}} - extract the array
        setIntegrations(Array.isArray(intData) ? intData : (intData?.integrations || []));
      }
      if (rubRes.status === 'fulfilled') setRubriche(rubRes.value.data || []);
      if (pplRes.status === 'fulfilled') setPeople(pplRes.value.data || []);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Load products when switching to products section
  useEffect(() => {
    if (activeSection === 'products' && products.length === 0) {
      loadProducts();
    }
  }, [activeSection]);

  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.patch('/company/profile', {
        name: form.name, tagline: form.tagline, description: form.description,
        sector: form.sector, primary_market: form.primaryMarket,
        secondary_markets: form.secondaryMarkets, location: form.location,
        founded_year: form.foundedYear, languages: form.languages,
        positioning: form.positioning, currency: form.currency,
        tone_of_voice: form.toneOfVoice,
        keywords_use: form.wordsToUse, keywords_avoid: form.wordsToAvoid,
        brand: {
          primary_color: form.primaryColor, accent_color: form.accentColor,
          background_color: form.backgroundColor, primary_font: form.primaryFont,
          secondary_font: form.secondaryFont, tone_of_voice: form.toneOfVoice,
          words_to_use: form.wordsToUse, words_to_avoid: form.wordsToAvoid,
        },
      });
      toast.success('Salvato');
    } catch { toast.error('Errore salvataggio'); }
    setSaving(false);
  };

  // Products CRUD
  const loadProducts = async () => {
    try {
      const res = await api.get('/offering/products');
      setProducts(res.data || []);
    } catch {}
  };
  const saveProduct = async () => {
    setSaving(true);
    try {
      if (editingProduct) {
        await api.patch(`/offering/products/${editingProduct}`, productForm);
        toast.success('Prodotto aggiornato');
      } else {
        await api.post('/offering/products', productForm);
        toast.success('Prodotto creato');
      }
      setShowProductDialog(false);
      setEditingProduct(null);
      setProductForm({ name: '', category: 'formazione', product_type: 'workshop', pricing_model: 'per_partecipante', price: '', description: '', status: 'in_sviluppo' });
      await loadProducts();
    } catch { toast.error('Errore salvataggio prodotto'); }
    setSaving(false);
  };
  const deleteProduct = async (id) => {
    try {
      await api.delete(`/offering/products/${id}`);
      toast.success('Prodotto eliminato');
      await loadProducts();
    } catch { toast.error('Errore eliminazione'); }
  };
  const openEditProduct = (p) => {
    setEditingProduct(p.id);
    setProductForm({ name: p.name || '', category: p.category || 'formazione', product_type: p.product_type || 'workshop', pricing_model: p.pricing_model || 'per_partecipante', price: String(p.price || p.price_chf || ''), description: p.description || '', status: p.status || 'in_sviluppo' });
    setShowProductDialog(true);
  };

  // Social profile CRUD
  const addSocialProfile = async () => {
    try {
      await api.post('/social-profiles', profileForm);
      toast.success('Profilo aggiunto');
      setShowProfileDialog(false);
      setProfileForm({ platform: 'LinkedIn', handle: '', url: '' });
      const res = await api.get('/social-profiles');
      setChannels(res.data || []);
    } catch { toast.error('Errore aggiunta profilo'); }
  };

  // Rubrica CRUD
  const addRubrica = async () => {
    try {
      await api.post('/content/rubriche', rubricaForm);
      toast.success('Rubrica creata');
      setShowRubricaDialog(false);
      setRubricaForm({ name: '', code: '', channel: 'LinkedIn personale', frequency: 'settimanale', day: 'Lunedi' });
      const res = await api.get('/content/rubriche');
      setRubriche(res.data || []);
    } catch { toast.error('Errore creazione rubrica'); }
  };

  // Document upload
  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', 'knowledge');
    try {
      await api.post('/designer/assets/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`"${file.name}" caricato`);
      setShowDocDialog(false);
    } catch { toast.info('Upload documento (endpoint in sviluppo)'); setShowDocDialog(false); }
  };

  // Collaborator invite
  const inviteCollaborator = async () => {
    try {
      await api.post('/company/people', collabForm);
      toast.success('Collaboratore invitato');
      setShowCollabDialog(false);
      setCollabForm({ name: '', email: '', role: 'editor' });
      const res = await api.get('/company/people');
      setPeople(res.data || []);
    } catch { toast.info('Invito collaboratore (endpoint in sviluppo)'); setShowCollabDialog(false); }
  };

  const completionItems = [
    { id: 'identity', label: 'Identita progetto', complete: !!form.name && !!form.description },
    { id: 'brand', label: 'Brand & identita visiva', complete: !!form.toneOfVoice },
    { id: 'channels', label: 'Canali collegati', complete: channels.length > 0, partial: `${channels.length} profili` },
    { id: 'products', label: 'Catalogo prodotti', complete: products.length > 0, partial: `${products.length} prodotti` },
    { id: 'documents', label: 'Knowledge Base', complete: false, partial: '0 documenti' },
    { id: 'rubriche', label: 'Rubriche editoriali', complete: rubriche.length > 0, partial: `${rubriche.length} rubriche` },
    { id: 'team', label: 'Team', complete: people.length > 1, partial: `${people.length} persone` },
  ];

  const completedCount = completionItems.filter(i => i.complete).length;
  const completionPct = Math.round((completedCount / completionItems.length) * 100);

  if (loading) return (
    <div className="flex h-full items-center justify-center" data-testid="workspace-loading">
      <Loader2 className="h-8 w-8 animate-spin" style={{ color: '#2563EB' }} />
    </div>
  );

  return (
    <div className="flex h-[calc(100vh-64px)]" data-testid="workspace-page">
      {/* Left Panel */}
      <div className="w-[280px] flex-shrink-0 overflow-y-auto p-5" style={{ borderRight: '1px solid #E5E7EB', backgroundColor: '#FFFFFF' }}>
        {/* Progress */}
        <div className="mb-6">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[13px] font-medium" style={{ color: '#1B2B4B' }}>Setup progetto</span>
            <span className="text-[13px] font-bold" style={{ color: '#2563EB' }}>{completionPct}%</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full" style={{ backgroundColor: '#E5E7EB' }}>
            <div className="h-full rounded-full transition-all" style={{ width: `${completionPct}%`, backgroundColor: '#2563EB' }} />
          </div>
          <p className="mt-2 text-[11px]" style={{ color: '#7C8CA2' }}>Completa il setup per migliorare la qualita degli agenti</p>
        </div>

        {/* Checklist */}
        <div className="mb-6 space-y-1">
          {completionItems.map(item => (
            <button key={item.id} onClick={() => setActiveSection(item.id)}
              className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-gray-50">
              {item.complete ? <Check className="h-4 w-4" style={{ color: '#22C55E' }} /> : <AlertCircle className="h-4 w-4" style={{ color: '#F59E0B' }} />}
              <span className="flex-1 text-[12px]" style={{ color: '#1B2B4B' }}>{item.label}</span>
              {item.partial && <span className="text-[10px]" style={{ color: '#7C8CA2' }}>{item.partial}</span>}
            </button>
          ))}
        </div>

        {/* Sections */}
        <div className="space-y-1">
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider" style={{ color: '#7C8CA2' }}>Sezioni</p>
          {SECTION_DEFS.map(sec => (
            <button key={sec.id} onClick={() => setActiveSection(sec.id)}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors"
              style={{ backgroundColor: activeSection === sec.id ? '#EFF6FF' : 'transparent', borderLeft: `3px solid ${activeSection === sec.id ? '#2563EB' : 'transparent'}` }}
              data-testid={`section-${sec.id}`}>
              <span style={{ color: activeSection === sec.id ? '#2563EB' : '#7C8CA2' }}>{sec.icon}</span>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-[13px] font-medium" style={{ color: activeSection === sec.id ? '#2563EB' : '#1B2B4B' }}>{sec.title}</span>
                  {statusDot(completionItems.find(c => c.id === sec.id)?.complete)}
                </div>
                <p className="text-[11px]" style={{ color: '#7C8CA2' }}>{sec.description}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Right Panel */}
      <div className="flex-1 overflow-y-auto p-6" style={{ backgroundColor: '#FAFBFC' }}>
        <div className="mx-auto max-w-[800px]">
          {/* Identity */}
          {activeSection === 'identity' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-[20px] font-bold" style={{ color: '#1B2B4B' }}>Identita del progetto</h2>
                <p className="mt-1 text-[13px]" style={{ color: '#7C8CA2' }}>Questi dati vengono iniettati nel contesto di ogni agente.</p>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="mb-1.5 block text-[12px] font-medium" style={{ color: '#1B2B4B' }}>Nome progetto</label>
                    <input type="text" value={form.name || ''} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                      className="w-full rounded-lg px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-500" style={{ border: '1px solid #E5E7EB' }}
                      data-testid="ws-name-input" />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-[12px] font-medium" style={{ color: '#1B2B4B' }}>Settore</label>
                    <select value={form.sector || 'Formazione'} onChange={e => setForm(f => ({ ...f, sector: e.target.value }))}
                      className="w-full rounded-lg px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-500" style={{ border: '1px solid #E5E7EB' }}>
                      <option>Coaching</option><option>Formazione</option><option>Consulenza</option><option>Tech</option><option>Altro</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="mb-1.5 block text-[12px] font-medium" style={{ color: '#1B2B4B' }}>Tagline</label>
                  <input type="text" value={form.tagline || ''} onChange={e => setForm(f => ({ ...f, tagline: e.target.value }))}
                    className="w-full rounded-lg px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-500" style={{ border: '1px solid #E5E7EB' }} />
                </div>
                <div>
                  <label className="mb-1.5 block text-[12px] font-medium" style={{ color: '#1B2B4B' }}>Descrizione progetto</label>
                  <textarea rows={6} value={form.description || ''} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                    className="w-full rounded-lg px-3 py-2 text-[14px] leading-relaxed focus:outline-none focus:ring-2 focus:ring-blue-500" style={{ border: '1px solid #E5E7EB' }} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="mb-1.5 block text-[12px] font-medium" style={{ color: '#1B2B4B' }}>Mercato primario</label>
                    <input type="text" value={form.primaryMarket || ''} onChange={e => setForm(f => ({ ...f, primaryMarket: e.target.value }))}
                      className="w-full rounded-lg px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-500" style={{ border: '1px solid #E5E7EB' }} />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-[12px] font-medium" style={{ color: '#1B2B4B' }}>Mercati secondari</label>
                    <input type="text" value={form.secondaryMarkets || ''} onChange={e => setForm(f => ({ ...f, secondaryMarkets: e.target.value }))}
                      className="w-full rounded-lg px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-500" style={{ border: '1px solid #E5E7EB' }} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="mb-1.5 block text-[12px] font-medium" style={{ color: '#1B2B4B' }}>Sede</label>
                    <input type="text" value={form.location || ''} onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
                      className="w-full rounded-lg px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-500" style={{ border: '1px solid #E5E7EB' }} />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-[12px] font-medium" style={{ color: '#1B2B4B' }}>Anno di fondazione</label>
                    <input type="text" value={form.foundedYear || ''} onChange={e => setForm(f => ({ ...f, foundedYear: e.target.value }))}
                      className="w-full rounded-lg px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-500" style={{ border: '1px solid #E5E7EB' }} />
                  </div>
                </div>
                {/* Posizionamento & Valuta */}
                <div>
                  <label className="mb-1.5 block text-[12px] font-medium" style={{ color: '#1B2B4B' }}>Come ci presentiamo</label>
                  <textarea rows={4} value={form.positioning || ''} onChange={e => setForm(f => ({ ...f, positioning: e.target.value }))}
                    placeholder="Es: Non vendiamo agenti AI — aiutiamo professionisti a usare l'AI per crescere. Il nostro focus è formazione pratica e community..."
                    className="w-full rounded-lg px-3 py-2 text-[14px] leading-relaxed focus:outline-none focus:ring-2 focus:ring-blue-500" style={{ border: '1px solid #E5E7EB' }}
                    data-testid="ws-positioning" />
                  <p className="mt-1 text-[11px]" style={{ color: '#7C8CA2' }}>Questo campo guida il Copywriter nel posizionamento dei contenuti</p>
                </div>
                <div>
                  <label className="mb-1.5 block text-[12px] font-medium" style={{ color: '#1B2B4B' }}>Tono di voce</label>
                  <textarea rows={3} value={form.toneOfVoice || ''} onChange={e => setForm(f => ({ ...f, toneOfVoice: e.target.value }))}
                    placeholder="Es: Professionale ma accessibile. Usiamo il 'tu'. Evitiamo gergo tecnico eccessivo. Siamo diretti e concreti."
                    className="w-full rounded-lg px-3 py-2 text-[14px] leading-relaxed focus:outline-none focus:ring-2 focus:ring-blue-500" style={{ border: '1px solid #E5E7EB' }}
                    data-testid="ws-tone" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="mb-1.5 block text-[12px] font-medium" style={{ color: '#1B2B4B' }}>Parole da usare</label>
                    <input type="text" value={Array.isArray(form.wordsToUse) ? form.wordsToUse.join(', ') : (form.wordsToUse || '')}
                      onChange={e => setForm(f => ({ ...f, wordsToUse: e.target.value.split(',').map(w => w.trim()).filter(Boolean) }))}
                      placeholder="crescita, pratico, concreto, community"
                      className="w-full rounded-lg px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-500" style={{ border: '1px solid #E5E7EB' }}
                      data-testid="ws-words-use" />
                    <p className="mt-1 text-[11px]" style={{ color: '#7C8CA2' }}>Separate da virgola</p>
                  </div>
                  <div>
                    <label className="mb-1.5 block text-[12px] font-medium" style={{ color: '#1B2B4B' }}>Espressioni da evitare</label>
                    <input type="text" value={Array.isArray(form.wordsToAvoid) ? form.wordsToAvoid.join(', ') : (form.wordsToAvoid || '')}
                      onChange={e => setForm(f => ({ ...f, wordsToAvoid: e.target.value.split(',').map(w => w.trim()).filter(Boolean) }))}
                      placeholder="agenti AI, automazione, disruptive"
                      className="w-full rounded-lg px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-500" style={{ border: '1px solid #E5E7EB' }}
                      data-testid="ws-words-avoid" />
                    <p className="mt-1 text-[11px]" style={{ color: '#7C8CA2' }}>Separate da virgola</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="mb-1.5 block text-[12px] font-medium" style={{ color: '#1B2B4B' }}>Valuta</label>
                    <select value={form.currency || 'CHF'} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}
                      className="w-full rounded-lg px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-500" style={{ border: '1px solid #E5E7EB' }}
                      data-testid="ws-currency">
                      <option value="CHF">CHF — Franco svizzero</option>
                      <option value="EUR">EUR — Euro</option>
                      <option value="USD">USD — Dollaro USA</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="flex justify-end">
                <button onClick={saveProfile} disabled={saving}
                  className="rounded-lg px-4 py-2 text-[13px] font-medium text-white disabled:opacity-50"
                  style={{ backgroundColor: '#2563EB' }} data-testid="ws-save-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Salva modifiche'}
                </button>
              </div>
            </div>
          )}

          {/* Brand */}
          {activeSection === 'brand' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-[20px] font-bold" style={{ color: '#1B2B4B' }}>Brand & identita visiva</h2>
                <p className="mt-1 text-[13px]" style={{ color: '#7C8CA2' }}>Il Designer Agent legge questi dati per generare immagini coerenti.</p>
              </div>
              <div>
                <label className="mb-2 block text-[12px] font-medium" style={{ color: '#1B2B4B' }}>Colori brand</label>
                <div className="grid grid-cols-3 gap-4">
                  {[{ key: 'primaryColor', label: 'Primario' }, { key: 'accentColor', label: 'Accento' }, { key: 'backgroundColor', label: 'Sfondo' }].map(c => (
                    <div key={c.key} className="rounded-lg p-3" style={{ border: '1px solid #E5E7EB' }}>
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg" style={{ backgroundColor: form[c.key] || '#ccc', border: c.key === 'backgroundColor' ? '1px solid #E5E7EB' : 'none' }} />
                        <div>
                          <p className="text-[11px]" style={{ color: '#7C8CA2' }}>{c.label}</p>
                          <input type="text" value={form[c.key] || ''} onChange={e => setForm(f => ({ ...f, [c.key]: e.target.value }))}
                            className="w-20 border-none p-0 text-[13px] font-mono focus:outline-none" style={{ color: '#1B2B4B' }} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1.5 block text-[12px] font-medium" style={{ color: '#1B2B4B' }}>Font primario</label>
                  <input type="text" value={form.primaryFont || ''} onChange={e => setForm(f => ({ ...f, primaryFont: e.target.value }))}
                    className="w-full rounded-lg px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-500" style={{ border: '1px solid #E5E7EB' }} />
                </div>
                <div>
                  <label className="mb-1.5 block text-[12px] font-medium" style={{ color: '#1B2B4B' }}>Font secondario</label>
                  <input type="text" value={form.secondaryFont || ''} onChange={e => setForm(f => ({ ...f, secondaryFont: e.target.value }))}
                    className="w-full rounded-lg px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-500" style={{ border: '1px solid #E5E7EB' }} />
                </div>
              </div>
              <div>
                <label className="mb-1.5 block text-[12px] font-medium" style={{ color: '#1B2B4B' }}>Tono di voce</label>
                <textarea rows={3} value={form.toneOfVoice || ''} onChange={e => setForm(f => ({ ...f, toneOfVoice: e.target.value }))}
                  className="w-full rounded-lg px-3 py-2 text-[14px] leading-relaxed focus:outline-none focus:ring-2 focus:ring-blue-500" style={{ border: '1px solid #E5E7EB' }} />
              </div>
              <div className="flex justify-end">
                <button onClick={saveProfile} disabled={saving} className="rounded-lg px-4 py-2 text-[13px] font-medium text-white disabled:opacity-50" style={{ backgroundColor: '#2563EB' }}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Salva modifiche'}
                </button>
              </div>
            </div>
          )}

          {/* Channels */}
          {activeSection === 'channels' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-[20px] font-bold" style={{ color: '#1B2B4B' }}>Canali e profili collegati</h2>
                <p className="mt-1 text-[13px]" style={{ color: '#7C8CA2' }}>Configura i tuoi profili social e le integrazioni API.</p>
              </div>
              <div>
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="text-[14px] font-semibold" style={{ color: '#1B2B4B' }}>Profili social</h3>
                  <button onClick={() => setShowProfileDialog(true)} className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12px] font-medium" style={{ backgroundColor: '#EFF6FF', color: '#2563EB' }}
                    data-testid="add-profile-btn">
                    <Plus className="h-3.5 w-3.5" /> Aggiungi profilo
                  </button>
                </div>
                <div className="space-y-2">
                  {channels.map((ch, i) => (
                    <div key={i} className="flex items-center justify-between rounded-lg p-3" style={{ border: '1px solid #E5E7EB' }}>
                      <div className="flex items-center gap-3">
                        <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: integrationColor(ch.status) }} />
                        <div>
                          <p className="text-[13px] font-medium" style={{ color: '#1B2B4B' }}>{ch.platform || ch.name}</p>
                          <p className="text-[11px]" style={{ color: '#7C8CA2' }}>{ch.owner || ch.url || ''}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {ch.url && <a href={ch.url} target="_blank" rel="noopener noreferrer" style={{ color: '#2563EB' }}><ExternalLink className="h-4 w-4" /></a>}
                        <Settings className="h-4 w-4" style={{ color: '#7C8CA2' }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="mb-3 text-[14px] font-semibold" style={{ color: '#1B2B4B' }}>Integrazioni API</h3>
                <div className="space-y-2">
                  {integrations.map((int, i) => (
                    <div key={i} className="flex items-center justify-between rounded-lg p-3" style={{ border: '1px solid #E5E7EB' }}>
                      <div className="flex items-center gap-3">
                        <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: integrationColor(int.status) }} />
                        <div>
                          <p className="text-[13px] font-medium" style={{ color: '#1B2B4B' }}>{int.name || int.integration_id}</p>
                          {int.details && <p className="text-[11px]" style={{ color: '#7C8CA2' }}>{int.details}</p>}
                        </div>
                      </div>
                      <button className="rounded-lg px-2 py-1 text-[11px] font-medium"
                        style={{ backgroundColor: int.status === 'connected' ? '#F3F4F6' : '#EFF6FF', color: int.status === 'connected' ? '#7C8CA2' : '#2563EB' }}>
                        {int.status === 'connected' ? <><RefreshCw className="mr-1 inline h-3 w-3" />Testa</> : 'Configura'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Products */}
          {activeSection === 'products' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-[20px] font-bold" style={{ color: '#1B2B4B' }}>Prodotti e servizi</h2>
                  <p className="mt-1 text-[13px]" style={{ color: '#7C8CA2' }}>Il catalogo che gli agenti usano per raccomandazioni.</p>
                </div>
                <button onClick={() => { setEditingProduct(null); setProductForm({ name: '', category: 'formazione', product_type: 'workshop', pricing_model: 'per_partecipante', price: '', description: '', status: 'in_sviluppo' }); setShowProductDialog(true); }}
                  className="flex items-center gap-1.5 rounded-lg px-4 py-2 text-[13px] font-medium text-white" style={{ backgroundColor: '#2563EB' }}
                  data-testid="new-product-btn">
                  <Plus className="h-4 w-4" /> Nuovo prodotto
                </button>
              </div>
              <div className="space-y-3">
                {products.map((p, i) => (
                  <div key={p.id || i} className="rounded-lg p-4" style={{ border: '1px solid #E5E7EB', backgroundColor: '#FFFFFF' }}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="text-[14px] font-semibold" style={{ color: '#1B2B4B' }}>{p.name}</h4>
                          <span className="rounded-full px-2 py-0.5 text-[10px] font-medium"
                            style={{ backgroundColor: p.status === 'active' || p.status === 'Attivo' ? '#D1FAE5' : p.status === 'in_sviluppo' ? '#FEF3C7' : '#F3F4F6', color: p.status === 'active' || p.status === 'Attivo' ? '#059669' : p.status === 'in_sviluppo' ? '#D97706' : '#7C8CA2' }}>
                            {p.status === 'active' ? 'Attivo' : p.status === 'in_sviluppo' ? 'In sviluppo' : p.status || 'Attivo'}
                          </span>
                        </div>
                        <div className="mt-1 flex items-center gap-4 text-[12px]" style={{ color: '#7C8CA2' }}>
                          <span>{p.category || p.type || p.product_type || ''}</span>
                          <span>{p.pricing_model || p.duration || ''}</span>
                        </div>
                        {p.description && <p className="mt-1 text-[12px] line-clamp-2" style={{ color: '#6B7280' }}>{p.description}</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        <p className="text-[16px] font-bold mr-2" style={{ color: '#1B2B4B' }}>
                          {p.price_chf ? `CHF ${p.price_chf}` : p.price || ''}
                        </p>
                        <button onClick={() => openEditProduct(p)} className="rounded p-1.5 hover:bg-gray-100 transition-colors" data-testid={`edit-product-${p.id || i}`}>
                          <Edit3 className="h-4 w-4" style={{ color: '#6B7280' }} />
                        </button>
                        <button onClick={() => deleteProduct(p.id)} className="rounded p-1.5 hover:bg-red-50 transition-colors" data-testid={`delete-product-${p.id || i}`}>
                          <Trash2 className="h-4 w-4" style={{ color: '#DC2626' }} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                {products.length === 0 && <p className="text-center text-[13px] py-8" style={{ color: '#9CA3AF' }}>Nessun prodotto configurato</p>}
              </div>
            </div>
          )}

          {/* Documents/Knowledge Base */}
          {activeSection === 'documents' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-[20px] font-bold" style={{ color: '#1B2B4B' }}>Knowledge Base</h2>
                  <p className="mt-1 text-[13px]" style={{ color: '#7C8CA2' }}>Documenti che gli agenti usano come contesto.</p>
                </div>
                <button onClick={() => setShowDocDialog(true)} className="flex items-center gap-1.5 rounded-lg px-4 py-2 text-[13px] font-medium text-white" style={{ backgroundColor: '#2563EB' }}
                  data-testid="upload-doc-btn">
                  <Upload className="h-4 w-4" /> Carica documento
                </button>
              </div>
              <div className="flex items-center justify-center rounded-lg p-8" style={{ border: '2px dashed #E5E7EB' }}>
                <div className="text-center">
                  <Upload className="mx-auto h-8 w-8" style={{ color: '#D1D5DB' }} />
                  <p className="mt-2 text-[13px]" style={{ color: '#7C8CA2' }}>Trascina qui i file o clicca per caricare</p>
                  <p className="mt-1 text-[11px]" style={{ color: '#A3A3A3' }}>PDF, DOCX, TXT (max 10MB)</p>
                </div>
              </div>
            </div>
          )}

          {/* Rubriche */}
          {activeSection === 'rubriche' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-[20px] font-bold" style={{ color: '#1B2B4B' }}>Rubriche editoriali</h2>
                  <p className="mt-1 text-[13px]" style={{ color: '#7C8CA2' }}>Il Planner e il Copywriter usano le rubriche per generare il piano.</p>
                </div>
                <button onClick={() => setShowRubricaDialog(true)} className="flex items-center gap-1.5 rounded-lg px-4 py-2 text-[13px] font-medium text-white" style={{ backgroundColor: '#2563EB' }}
                  data-testid="new-rubrica-btn">
                  <Plus className="h-4 w-4" /> Nuova rubrica
                </button>
              </div>
              <div className="space-y-2">
                {rubriche.map((r, i) => (
                  <div key={i} className="flex items-center justify-between rounded-lg p-4"
                    style={{ border: '1px solid #E5E7EB', backgroundColor: (r.active !== false) ? '#FFFFFF' : '#FAFAFA', opacity: (r.active !== false) ? 1 : 0.7 }}>
                    <div className="flex items-center gap-4">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg text-[14px] font-bold" style={{ backgroundColor: '#EFF6FF', color: '#2563EB' }}>
                        {r.code || r.rubric_code || 'R'}
                      </div>
                      <div>
                        <p className="text-[14px] font-medium" style={{ color: '#1B2B4B' }}>{r.name}</p>
                        <div className="flex items-center gap-2 text-[11px]" style={{ color: '#7C8CA2' }}>
                          <span>{r.channel || r.platform}</span>
                          {r.frequency && <><span>&middot;</span><span>{r.frequency}</span></>}
                          {r.day && <><span>&middot;</span><span>{r.day}</span></>}
                        </div>
                      </div>
                    </div>
                    <span className="rounded-full px-2 py-0.5 text-[10px] font-medium"
                      style={{ backgroundColor: r.active !== false ? '#D1FAE5' : '#F3F4F6', color: r.active !== false ? '#059669' : '#7C8CA2' }}>
                      {r.active !== false ? 'Attiva' : 'Inattiva'}
                    </span>
                  </div>
                ))}
                {rubriche.length === 0 && <p className="text-center text-[13px] py-8" style={{ color: '#9CA3AF' }}>Nessuna rubrica configurata</p>}
              </div>
            </div>
          )}

          {/* Team */}
          {activeSection === 'team' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-[20px] font-bold" style={{ color: '#1B2B4B' }}>Team e collaboratori</h2>
                  <p className="mt-1 text-[13px]" style={{ color: '#7C8CA2' }}>Gestisci chi ha accesso al progetto.</p>
                </div>
                <button onClick={() => setShowCollabDialog(true)} className="flex items-center gap-1.5 rounded-lg px-4 py-2 text-[13px] font-medium text-white" style={{ backgroundColor: '#2563EB' }}
                  data-testid="invite-collab-btn">
                  <Plus className="h-4 w-4" /> Invita collaboratore
                </button>
              </div>
              <div className="space-y-2">
                {people.map((m, i) => (
                  <div key={i} className="flex items-center justify-between rounded-lg p-4" style={{ border: '1px solid #E5E7EB', backgroundColor: '#FFFFFF' }}>
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full text-[14px] font-bold text-white" style={{ backgroundColor: '#2563EB' }}>
                        {(m.name || 'U').split(' ').map(n => n[0]).join('').slice(0, 2)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-[14px] font-medium" style={{ color: '#1B2B4B' }}>{m.name}</p>
                          <span className="rounded-full px-2 py-0.5 text-[10px] font-medium" style={{ backgroundColor: '#DBEAFE', color: '#2563EB' }}>
                            {m.role || 'Member'}
                          </span>
                        </div>
                        <p className="text-[12px]" style={{ color: '#7C8CA2' }}>{m.email}</p>
                      </div>
                    </div>
                    {m.areas?.length > 0 && (
                      <div className="flex items-center gap-2">
                        {m.areas.map(a => (
                          <span key={a} className="rounded-full px-2 py-0.5 text-[10px]" style={{ backgroundColor: '#F3F4F6', color: '#7C8CA2' }}>{a}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {people.length <= 1 && (
                <div className="rounded-lg p-4" style={{ borderColor: '#FCD34D', backgroundColor: '#FFFBEB', border: '1px solid #FCD34D' }}>
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 flex-shrink-0" style={{ color: '#F59E0B' }} />
                    <div>
                      <p className="text-[13px] font-medium" style={{ color: '#92400E' }}>Lavori da solo su questo progetto</p>
                      <p className="mt-1 text-[12px]" style={{ color: '#B45309' }}>Invita un collaboratore per condividere il carico di lavoro.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ═══ DIALOGS ═══ */}
      {/* Add Social Profile */}
      <Dialog open={showProfileDialog} onOpenChange={setShowProfileDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Aggiungi profilo social</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <label className="mb-1 block text-[12px] font-medium" style={{ color: '#1B2B4B' }}>Piattaforma</label>
              <select value={profileForm.platform} onChange={e => setProfileForm(f => ({ ...f, platform: e.target.value }))}
                className="w-full rounded-lg px-3 py-2 text-[14px]" style={{ border: '1px solid #E5E7EB' }}>
                <option>LinkedIn</option><option>Instagram</option><option>Facebook</option><option>Twitter</option><option>Telegram</option><option>YouTube</option><option>TikTok</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-[12px] font-medium" style={{ color: '#1B2B4B' }}>Handle / Nome</label>
              <input type="text" value={profileForm.handle} onChange={e => setProfileForm(f => ({ ...f, handle: e.target.value }))}
                placeholder="@username" className="w-full rounded-lg px-3 py-2 text-[14px]" style={{ border: '1px solid #E5E7EB' }}
                data-testid="profile-handle-input" />
            </div>
            <div>
              <label className="mb-1 block text-[12px] font-medium" style={{ color: '#1B2B4B' }}>URL profilo</label>
              <input type="text" value={profileForm.url} onChange={e => setProfileForm(f => ({ ...f, url: e.target.value }))}
                placeholder="https://..." className="w-full rounded-lg px-3 py-2 text-[14px]" style={{ border: '1px solid #E5E7EB' }} />
            </div>
            <button onClick={addSocialProfile} disabled={!profileForm.handle}
              className="w-full rounded-lg py-2 text-[14px] font-medium text-white disabled:opacity-50" style={{ backgroundColor: '#2563EB' }}
              data-testid="save-profile-btn">Aggiungi profilo</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Rubrica */}
      <Dialog open={showRubricaDialog} onOpenChange={setShowRubricaDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Nuova rubrica editoriale</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-[12px] font-medium" style={{ color: '#1B2B4B' }}>Nome</label>
                <input type="text" value={rubricaForm.name} onChange={e => setRubricaForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Es: Pensiero del lunedi" className="w-full rounded-lg px-3 py-2 text-[14px]" style={{ border: '1px solid #E5E7EB' }}
                  data-testid="rubrica-name-input" />
              </div>
              <div>
                <label className="mb-1 block text-[12px] font-medium" style={{ color: '#1B2B4B' }}>Codice</label>
                <input type="text" value={rubricaForm.code} onChange={e => setRubricaForm(f => ({ ...f, code: e.target.value }))}
                  placeholder="R1" className="w-full rounded-lg px-3 py-2 text-[14px]" style={{ border: '1px solid #E5E7EB' }} />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-[12px] font-medium" style={{ color: '#1B2B4B' }}>Canale</label>
              <select value={rubricaForm.channel} onChange={e => setRubricaForm(f => ({ ...f, channel: e.target.value }))}
                className="w-full rounded-lg px-3 py-2 text-[14px]" style={{ border: '1px solid #E5E7EB' }}>
                <option>LinkedIn personale</option><option>LinkedIn aziendale</option><option>Newsletter</option><option>Instagram</option><option>Telegram</option><option>Email</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-[12px] font-medium" style={{ color: '#1B2B4B' }}>Frequenza</label>
                <select value={rubricaForm.frequency} onChange={e => setRubricaForm(f => ({ ...f, frequency: e.target.value }))}
                  className="w-full rounded-lg px-3 py-2 text-[14px]" style={{ border: '1px solid #E5E7EB' }}>
                  <option value="giornaliera">Giornaliera</option><option value="settimanale">Settimanale</option><option value="bisettimanale">Bisettimanale</option><option value="mensile">Mensile</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-[12px] font-medium" style={{ color: '#1B2B4B' }}>Giorno</label>
                <select value={rubricaForm.day} onChange={e => setRubricaForm(f => ({ ...f, day: e.target.value }))}
                  className="w-full rounded-lg px-3 py-2 text-[14px]" style={{ border: '1px solid #E5E7EB' }}>
                  <option>Lunedi</option><option>Martedi</option><option>Mercoledi</option><option>Giovedi</option><option>Venerdi</option>
                </select>
              </div>
            </div>
            <button onClick={addRubrica} disabled={!rubricaForm.name}
              className="w-full rounded-lg py-2 text-[14px] font-medium text-white disabled:opacity-50" style={{ backgroundColor: '#2563EB' }}
              data-testid="save-rubrica-btn">Crea rubrica</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Product Create/Edit */}
      <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editingProduct ? 'Modifica prodotto' : 'Nuovo prodotto'}</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <label className="mb-1 block text-[12px] font-medium" style={{ color: '#1B2B4B' }}>Nome</label>
              <input type="text" value={productForm.name} onChange={e => setProductForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Es: Workshop AI per PMI" className="w-full rounded-lg px-3 py-2 text-[14px]" style={{ border: '1px solid #E5E7EB' }}
                data-testid="product-name-input" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="mb-1 block text-[12px] font-medium" style={{ color: '#1B2B4B' }}>Categoria</label>
                <select value={productForm.category} onChange={e => setProductForm(f => ({ ...f, category: e.target.value }))}
                  className="w-full rounded-lg px-3 py-2 text-[14px]" style={{ border: '1px solid #E5E7EB' }}>
                  <option value="formazione">Formazione</option><option value="consulenza">Consulenza</option><option value="eventi">Eventi</option><option value="membership">Membership</option><option value="digitale">Digitale</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-[12px] font-medium" style={{ color: '#1B2B4B' }}>Tipo</label>
                <select value={productForm.product_type} onChange={e => setProductForm(f => ({ ...f, product_type: e.target.value }))}
                  className="w-full rounded-lg px-3 py-2 text-[14px]" style={{ border: '1px solid #E5E7EB' }}>
                  <option value="workshop">Workshop</option><option value="corso">Corso</option><option value="webinar">Webinar</option><option value="consulenza_1to1">Consulenza 1:1</option><option value="membership">Membership</option><option value="ebook">eBook</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-[12px] font-medium" style={{ color: '#1B2B4B' }}>Prezzo (CHF)</label>
                <input type="text" value={productForm.price} onChange={e => setProductForm(f => ({ ...f, price: e.target.value }))}
                  placeholder="490" className="w-full rounded-lg px-3 py-2 text-[14px]" style={{ border: '1px solid #E5E7EB' }} />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-[12px] font-medium" style={{ color: '#1B2B4B' }}>Stato</label>
              <select value={productForm.status} onChange={e => setProductForm(f => ({ ...f, status: e.target.value }))}
                className="w-full rounded-lg px-3 py-2 text-[14px]" style={{ border: '1px solid #E5E7EB' }}>
                <option value="in_sviluppo">In sviluppo</option><option value="active">Attivo</option><option value="sospeso">Sospeso</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-[12px] font-medium" style={{ color: '#1B2B4B' }}>Descrizione</label>
              <textarea rows={3} value={productForm.description} onChange={e => setProductForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Breve descrizione del prodotto..." className="w-full rounded-lg px-3 py-2 text-[14px]" style={{ border: '1px solid #E5E7EB' }} />
            </div>
            <button onClick={saveProduct} disabled={!productForm.name || saving}
              className="w-full rounded-lg py-2 text-[14px] font-medium text-white disabled:opacity-50" style={{ backgroundColor: '#2563EB' }}
              data-testid="save-product-btn">{saving ? 'Salvataggio...' : editingProduct ? 'Aggiorna prodotto' : 'Crea prodotto'}</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload Document */}
      <Dialog open={showDocDialog} onOpenChange={setShowDocDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Carica documento</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="flex items-center justify-center rounded-lg p-6 cursor-pointer" style={{ border: '2px dashed #D1D5DB' }}
              onClick={() => document.getElementById('doc-upload-input')?.click()}>
              <div className="text-center">
                <Upload className="mx-auto h-8 w-8" style={{ color: '#D1D5DB' }} />
                <p className="mt-2 text-[13px]" style={{ color: '#7C8CA2' }}>Clicca per selezionare un file</p>
                <p className="mt-1 text-[11px]" style={{ color: '#A3A3A3' }}>PDF, DOCX, TXT (max 10MB)</p>
              </div>
            </div>
            <input id="doc-upload-input" type="file" accept=".pdf,.docx,.txt,.doc" onChange={handleDocUpload} className="hidden"
              data-testid="doc-upload-input" />
          </div>
        </DialogContent>
      </Dialog>

      {/* Invite Collaborator */}
      <Dialog open={showCollabDialog} onOpenChange={setShowCollabDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Invita collaboratore</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <label className="mb-1 block text-[12px] font-medium" style={{ color: '#1B2B4B' }}>Nome</label>
              <input type="text" value={collabForm.name} onChange={e => setCollabForm(f => ({ ...f, name: e.target.value }))}
                className="w-full rounded-lg px-3 py-2 text-[14px]" style={{ border: '1px solid #E5E7EB' }}
                data-testid="collab-name-input" />
            </div>
            <div>
              <label className="mb-1 block text-[12px] font-medium" style={{ color: '#1B2B4B' }}>Email</label>
              <input type="email" value={collabForm.email} onChange={e => setCollabForm(f => ({ ...f, email: e.target.value }))}
                className="w-full rounded-lg px-3 py-2 text-[14px]" style={{ border: '1px solid #E5E7EB' }}
                data-testid="collab-email-input" />
            </div>
            <div>
              <label className="mb-1 block text-[12px] font-medium" style={{ color: '#1B2B4B' }}>Ruolo</label>
              <select value={collabForm.role} onChange={e => setCollabForm(f => ({ ...f, role: e.target.value }))}
                className="w-full rounded-lg px-3 py-2 text-[14px]" style={{ border: '1px solid #E5E7EB' }}>
                <option value="admin">Admin</option><option value="editor">Editor</option><option value="viewer">Viewer</option>
              </select>
            </div>
            <button onClick={inviteCollaborator} disabled={!collabForm.email}
              className="w-full rounded-lg py-2 text-[14px] font-medium text-white disabled:opacity-50" style={{ backgroundColor: '#2563EB' }}
              data-testid="save-collab-btn">Invita collaboratore</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
